import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './DatosPage.module.css';

const TABS: { key: string; label: string; count: number; warning?: boolean }[] = [
  { key: 'contactName', label: 'Sin nombre', count: 195 },
  { key: 'email', label: 'Sin email', count: 87 },
  { key: 'infoOnly', label: 'Solo info@', count: 44, warning: true },
  { key: 'pain', label: 'Sin dolor', count: 167 },
  { key: 'angle', label: 'Sin ángulo', count: 143 },
  { key: 'web', label: 'Sin web', count: 9 },
];

const TIERS: { key: string; label: string; count?: number }[] = [
  { key: 't1', label: 'T1', count: 217 },
  { key: 't2', label: 'T2', count: 712 },
  { key: 't3', label: 'T3', count: 868 },
  { key: 'all', label: 'Todos' },
];

interface Row {
  name: string;
  sub: string;
  tier: 't1' | 't2' | 't3';
  contactName: boolean;
  email: 'ok' | 'missing' | 'info';
  web: boolean;
  pain: boolean;
  angle: boolean;
}

const MOCK_ROWS: Row[] = [
  { name: 'Bodegas Valmiñor', sub: 'D.O. Rías Baixas · Pontevedra', tier: 't1', contactName: false, email: 'ok', web: true, pain: false, angle: true },
  { name: 'Mel de Galicia SL', sub: 'Apicultura · Ourense', tier: 't1', contactName: false, email: 'info', web: true, pain: true, angle: false },
  { name: 'Queixería O Cebreiro', sub: 'Lácteos artesanais · Lugo', tier: 't1', contactName: false, email: 'missing', web: true, pain: false, angle: false },
  { name: 'Conservas A Costa', sub: 'Conservas · A Coruña', tier: 't2', contactName: false, email: 'ok', web: true, pain: true, angle: true },
  { name: 'D.O. Ribeiro Consello', sub: 'Vino · Ourense', tier: 't2', contactName: false, email: 'missing', web: false, pain: false, angle: false },
];

function completeness(row: Row): number {
  const fields = [row.contactName, row.email === 'ok', row.web, row.pain, row.angle];
  const ok = fields.filter(Boolean).length;
  return Math.round((ok / fields.length) * 100);
}

export default function DatosPage() {
  const [activeTab, setActiveTab] = useState<string>('contactName');
  const [activeTier, setActiveTier] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    let rows = [...MOCK_ROWS];
    if (activeTier !== 'all') rows = rows.filter((r) => r.tier === activeTier);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q) || r.sub.toLowerCase().includes(q));
    }
    return rows;
  }, [activeTier, search]);

  const activeTabLabel = TABS.find((t) => t.key === activeTab)?.label ?? '';
  const activeCount = TABS.find((t) => t.key === activeTab)?.count ?? 0;

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarBrand}>
          <img
            src="https://logos.grandeandgordo.com/logoggblanco.png"
            alt="Grande&Gordo"
            className={styles.sidebarLogo}
          />
          <div>
            <div className={styles.sidebarBrandName}>Grande&Gordo</div>
            <div className={styles.sidebarBrandSub}>CRM · Leads</div>
          </div>
        </div>
        <nav className={styles.sidebarNav}>
          <Link to="/today">Hoy</Link>
          <Link to="/pipeline">Pipeline</Link>
          <Link to="/datados" className={styles.active}>Datos</Link>
          <Link to="/templates">Templates</Link>
        </nav>
      </aside>

      <main className={styles.main}>
        <div className={styles.pageHeader}>
          <h1>Datos</h1>
          <span className={styles.subtitle}>Completa los campos que faltan para poder contactar</span>
        </div>

        <div className={styles.pillsBar}>
          <span className={styles.pillsLabel}>Faltan:</span>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.pill} ${activeTab === tab.key ? styles.pillActive : ''} ${tab.warning ? styles.pillWarning : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className={styles.pillCount}>{tab.count}</span>
            </button>
          ))}
          <div className={styles.pillsMeta}>
            Mostrando <strong>{activeCount} leads</strong> · columna activa: <strong>{activeTab}</strong>
          </div>
        </div>

        <div className={styles.filterBar}>
          <span className={styles.filterLabel}>TIER:</span>
          {TIERS.map((tier) => (
            <button
              key={tier.key}
              className={`${styles.tierBtn} ${activeTier === tier.key ? styles.tierBtnActive : ''}`}
              onClick={() => setActiveTier(tier.key)}
            >
              {tier.label}
              {tier.count ? ` · ${tier.count}` : ''}
            </button>
          ))}
          <input
            className={styles.searchInput}
            placeholder="Buscar empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={styles.tableWrap}>
          <table>
            <thead>
              <tr>
                <th style={{ minWidth: 180 }}>Empresa</th>
                <th>Tier</th>
                <th className={styles.activeCol} style={{ minWidth: 120 }}>Nombre ▾</th>
                <th style={{ minWidth: 120 }}>Email</th>
                <th style={{ minWidth: 90 }}>Web</th>
                <th style={{ minWidth: 90 }}>Dolor</th>
                <th style={{ minWidth: 90 }}>Ángulo</th>
                <th style={{ minWidth: 110 }}>Completitud</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const pct = completeness(row);
                const barColor = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#e94560';
                return (
                  <tr key={row.name} className={styles.row}>
                    <td>
                      <div className={styles.companyName}>{row.name}</div>
                      <div className={styles.companySub}>{row.sub}</div>
                    </td>
                    <td>
                      <span className={`${styles.badgeTier} ${styles[`badgeTier${row.tier.toUpperCase()}`]}`}>
                        {row.tier.toUpperCase()}
                      </span>
                    </td>
                    <td className={styles.cellActiveCol}>
                      {row.contactName ? (
                        <span className={styles.cellOk}>✓</span>
                      ) : (
                        <span className={styles.cellMissing}>+ Añadir</span>
                      )}
                    </td>
                    <td>
                      {row.email === 'ok' ? (
                        <span className={styles.cellOk}>✓</span>
                      ) : row.email === 'info' ? (
                        <span className={styles.cellGeneric}>⚠ info@</span>
                      ) : (
                        <span className={styles.cellMissing}>+ Añadir</span>
                      )}
                    </td>
                    <td>{row.web ? <span className={styles.cellOk}>✓</span> : <span className={styles.cellMissing}>+ Añadir</span>}</td>
                    <td>{row.pain ? <span className={styles.cellOk}>✓</span> : <span className={styles.cellMissing}>+ Añadir</span>}</td>
                    <td>{row.angle ? <span className={styles.cellOk}>✓</span> : <span className={styles.cellMissing}>+ Añadir</span>}</td>
                    <td>
                      <div className={styles.completeness}>
                        <div className={styles.compBar}>
                          <div className={styles.compFill} style={{ width: `${pct}%`, background: barColor }} />
                        </div>
                        <span className={styles.compPct} style={{ color: barColor }}>
                          {pct}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={styles.actionFooter}>
          <span className={styles.meta}>
            {activeCount} leads con {activeTabLabel.toLowerCase()} vacío · T1 prioritario
          </span>
          <button className={styles.btnGhost}>Exportar CSV</button>
          <button className={styles.btnPrimary}>Enriquecer selección con IA</button>
        </div>
      </main>
    </div>
  );
}
